import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Flex } from "theme-ui";

import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import UserContext from "context/User";
import { deleteProposal, getProposalSenders, type ProposalSender } from "api/Proposals";
import { addMoContact, getMoProposalPrefillMany, prepareMoCombined, sendMoCombined } from "api/MaintenanceOpportunities";

/**
 * COMBINE & SEND (2026-11-04, Jack): several sites one owner runs -> ONE
 * proposal, sent the same way as Review & send. Everyone already found on
 * any of the sites is listed (no new lookups); tick who gets it. Each person
 * gets their own email, link and lead; every site moves to Proposal sent and
 * joins the owner's lead as a location.
 */

const sub: React.CSSProperties = { fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey };
const label: React.CSSProperties = { display: "block", fontFamily: "Poppins", fontSize: 11, fontWeight: 600, color: "#555", margin: "14px 0 4px", textTransform: "uppercase", letterSpacing: 0.3 };
const input: React.CSSProperties = { width: "100%", boxSizing: "border-box", border: "1px solid #E4E7EB", borderRadius: 6, padding: "7px 10px", fontFamily: "Poppins", fontSize: 13 };
const check = { appearance: "auto", WebkitAppearance: "checkbox", width: 16, height: 16, minWidth: 16, margin: "2px 0 0", padding: 0, flex: "none", cursor: "pointer" } as React.CSSProperties;
const pickRow: React.CSSProperties = { display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid #F1F2F4", fontFamily: "Poppins", fontSize: 12, cursor: "pointer" };

type Person = { key: string; email: string | null; opportunityId: string; contact: string | null; title: string | null; site: string; selected: boolean; source?: string | null };
type Site = { opportunityId: string; name: string; city?: string | null; state?: string | null; yards?: number | null; owner?: string | null; proposal?: { id: string; status: string } | null };

function fill(text: string, name: string | null | undefined): string {
  const first = String(name ?? "").trim().split(/\s+/)[0] || "";
  return text.replace(/\{firstName\}/g, /^[A-Za-z][A-Za-z'-]+$/.test(first) ? first : "there");
}

const CombineSend: React.FC<{ ids: string[]; onClose: (sent: boolean) => void }> = ({ ids, onClose }) => {
  const toast = useToast();
  const { user } = useContext(UserContext) as any;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [skipped, setSkipped] = useState<Array<{ id: string; reason: string }>>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [owner, setOwner] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [includePassword, setIncludePassword] = useState(true);
  const [senders, setSenders] = useState<ProposalSender[]>([]);
  const [senderId, setSenderId] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ sent: number; proposalId: string } | null>(null);
  const ownerInText = React.useRef("");
  // The combined proposal, built for preview; attached to the sites only on Send.
  const [prepared, setPrepared] = useState<{ proposalId: string; shareUrl: string; order: string[]; owner: string } | null>(null);
  const [preparing, setPreparing] = useState(false);
  const preparedRef = React.useRef<string | null>(null);
  const prepare = async (siteIds: string[], ownerName: string) => {
    const old = preparedRef.current;
    preparedRef.current = null;
    setPrepared(null);
    if (old) deleteProposal(old);
    if (siteIds.length < 2) return;
    setPreparing(true);
    const r: any = await prepareMoCombined(siteIds, ownerName.trim() || null);
    setPreparing(false);
    if (r?.error) return setError(r.error);
    preparedRef.current = r.proposalId;
    setPrepared({ proposalId: r.proposalId, shareUrl: r.shareUrl, order: r.order, owner: ownerName.trim() });
  };
  const [emailFor, setEmailFor] = useState<Record<string, string>>({});
  const templateRef = React.useRef({ subject: "", message: "" });

  useEffect(() => {
    getProposalSenders().then((s) => {
      const list = Array.isArray(s) ? s : [];
      setSenders(list);
      if (user?.id && list.some((x) => x.id === user.id)) setSenderId(user.id);
    });
  }, [user?.id]);

  useEffect(() => {
    getMoProposalPrefillMany(ids).then((p: any) => {
      setLoading(false);
      if (!p || p.error) return setError(p?.error ?? "Could not load the sites.");
      setSites(p.sites ?? []);
      setSkipped(p.skipped ?? []);
      const list: Person[] = p.recipients ?? [];
      setPeople(list);
      const withEmail = list.filter((x) => x.email);
      const ticked = withEmail.filter((x) => x.selected).map((x) => x.key);
      setPicked(ticked.length ? ticked : withEmail.slice(0, 1).map((x) => x.key));
      setOwner(p.prospectName ?? "");
      ownerInText.current = (p.prospectName ?? "").trim();
      setSubject(p.email?.subject ?? "");
      setMessage(p.email?.message ?? "");
      templateRef.current = { subject: p.email?.subject ?? "", message: p.email?.message ?? "" };
      prepare((p.sites ?? []).map((x: Site) => x.opportunityId), p.prospectName ?? "");
    });
  }, []); // eslint-disable-line

  const renameOwner = () => {
    const next = owner.trim();
    const prev = ownerInText.current;
    if (!next || !prev || next === prev) return;
    setSubject((t) => t.split(prev).join(next));
    setMessage((t) => t.split(prev).join(next));
    templateRef.current = { subject: templateRef.current.subject.split(prev).join(next), message: templateRef.current.message.split(prev).join(next) };
    ownerInText.current = next;
    // The proposal carries the owner's name: rebuild the preview.
    prepare(sites.map((x) => x.opportunityId), next);
  };

  // Leave a site out (e.g. one no depot covers): reload the rest so the
  // site list, people and the drafted email all agree. A hand-edited email
  // and the owner are kept.
  const removeSite = (id: string) => {
    const rest = sites.map((x) => x.opportunityId).filter((x) => x !== id);
    const keepOwner = owner;
    const edited = message !== templateRef.current.message || subject !== templateRef.current.subject;
    const keepPicked = picked.filter((k) => !k.startsWith(`${id}::`));
    setLoading(true);
    getMoProposalPrefillMany(rest).then((p: any) => {
      setLoading(false);
      if (!p || p.error) return setError(p?.error ?? "Could not reload the sites.");
      setSites(p.sites ?? []);
      setSkipped(p.skipped ?? []);
      setPeople(p.recipients ?? []);
      setPicked(keepPicked);
      prepare((p.sites ?? []).map((x: Site) => x.opportunityId), keepOwner);
      if (!edited) {
        const swap = (t: string) => (ownerInText.current && keepOwner && p.prospectName ? t.split(p.prospectName).join(keepOwner) : t);
        templateRef.current = { subject: swap(p.email?.subject ?? ""), message: swap(p.email?.message ?? "") };
        setSubject(templateRef.current.subject);
        setMessage(templateRef.current.message);
      }
    });
  };

  const chosen = useMemo(() => people.filter((x) => picked.includes(x.key)), [people, picked]);
  const sentAlready = sites.filter((s) => s.proposal && !["ready", "draft"].includes(s.proposal.status));
  const toggle = (k: string) => {
    if (!people.find((x) => x.key === k)?.email) return;
    setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  };
  // A person found by name only: add their email on that site, then tick them.
  const giveEmail = async (c: Person) => {
    const email = (emailFor[c.key] ?? "").trim();
    if (!email) return;
    const r: any = await addMoContact(c.opportunityId, { contact: c.contact ?? undefined, title: c.title ?? undefined, email });
    if (r?.error) return toast.add(r.error, "var(--theme-ui-colors-danger)");
    const added = (r.trail ?? []).find((t: any) => (t.email ?? "").toLowerCase() === email.toLowerCase());
    if (!added) return;
    const key = `${c.opportunityId}::${added.key}`;
    setPeople((list) => list.map((x) => (x.key === c.key ? { ...x, key, email: email.toLowerCase() } : x)));
    setPicked((k) => [...k.filter((x) => x !== c.key), key]);
    setEmailFor((m) => ({ ...m, [c.key]: "" }));
  };
  const canSend = sites.length >= 2 && chosen.length > 0 && owner.trim() && subject.trim() && message.trim() && senderId && !busy && !preparing;

  const send = async () => {
    setBusy(true);
    setError(null);
    const ids = prepared?.order?.length === sites.length ? prepared.order : sites.map((s) => s.opportunityId);
    const r: any = await sendMoCombined({
      ids,
      preparedId: prepared && prepared.owner === owner.trim() ? prepared.proposalId : null,
      recipients: chosen.map((c) => c.key),
      subject, message, includePassword, ownerName: owner.trim(), senderUserId: senderId,
    });
    setBusy(false);
    if (r?.error) return setError(r.error);
    preparedRef.current = null;
    const failed = (r.results ?? []).filter((x: any) => !x.ok);
    if (failed.length) toast.add(`${failed.length} email${failed.length === 1 ? "" : "s"} failed: ${failed.map((f: any) => f.email).join(", ")}`, "var(--theme-ui-colors-danger)");
    setDone({ sent: r.sent ?? 0, proposalId: r.proposalId });
  };

  return (
    <>
      <Box sx={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 1000 }} />
      <Box role="dialog" sx={{ position: "fixed", top: ["0", "4vh"], left: "50%", transform: "translateX(-50%)", width: ["100%", "960px"], maxHeight: ["100vh", "92vh"], overflow: "auto", background: "#fff", borderRadius: [0, "10px"], zIndex: 1001, padding: "20px 24px", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}>
        <Flex sx={{ justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
          <div style={{ fontFamily: "Poppins", fontSize: 16, fontWeight: 600 }}>{`Combine ${sites.length || ids.length} sites into one proposal`}</div>
          <div style={sub}>One proposal · one email, link and lead per person</div>
        </Flex>

        {loading && <div style={{ ...sub, padding: "40px 0", textAlign: "center" }}>Loading the sites and everyone already found on them…</div>}

        {done && (
          <Box sx={{ padding: "30px 0", textAlign: "center", fontFamily: "Poppins", fontSize: 14 }}>
            {`Sent to ${done.sent} ${done.sent === 1 ? "person" : "people"}. All ${sites.length} sites moved to Proposal sent and sit on the owner's lead.`}
            <Flex sx={{ gap: "8px", justifyContent: "center", marginTop: "14px" }}>
              <BasicButton outline scheme="secondary" type="button" keepText onClick={() => window.open(`/proposals/${done.proposalId}`, "_blank")}>Open the proposal</BasicButton>
              <BasicButton type="button" keepText onClick={() => onClose(true)}>Close</BasicButton>
            </Flex>
          </Box>
        )}

        {!loading && !done && (
          <Flex sx={{ gap: "24px", flexDirection: ["column", "row"], marginTop: "10px" }}>
            <Box sx={{ flex: "0 0 380px", minWidth: 0 }}>
              <label style={label}>Sites in this proposal</label>
              {sites.map((s) => (
                <div key={s.opportunityId} style={{ ...pickRow, cursor: "default", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                    <b>{s.name}</b>
                    <span style={sub}>{[s.city, s.state].filter(Boolean).join(", ")}{s.yards ? ` · ${s.yards} yd` : ""}{s.owner ? ` · ${s.owner}` : ""}</span>
                  </div>
                  {sites.length > 2 && (
                    <button type="button" title="Leave this site out" onClick={() => removeSite(s.opportunityId)} style={{ border: "none", background: "none", color: themevals.colors.dkgrey, cursor: "pointer", fontSize: 12, textDecoration: "underline", flex: "none" }}>Remove</button>
                  )}
                </div>
              ))}
              {skipped.length > 0 && <div style={{ ...sub, color: "#A60000", marginTop: 6 }}>{`${skipped.length} left out: no map position, so they cannot be priced.`}</div>}
              {sentAlready.length > 0 && <div style={{ ...sub, marginTop: 6 }}>{`${sentAlready.length} of these already had a proposal sent. That one stays as it was; this replaces it as the current one.`}</div>}

              <label style={label}>Owner (one organization for every site)</label>
              <input style={input} value={owner} onChange={(e) => setOwner(e.target.value)} onBlur={renameOwner} placeholder="e.g. Guidepost Montessori" />
              <div style={{ ...sub, marginTop: 4 }}>Becomes the lead every person and site is filed under.</div>

              <label style={label}>{`Who gets it - everyone found on these sites (${people.length})`}</label>
              <div style={{ ...sub, marginBottom: 4 }}>{`${people.filter((x) => x.email).length} with an email. People found by name only need one added before they can be ticked.`}</div>
              {people.length === 0 && <div style={sub}>Nobody with an email has been found on these sites yet. Run Find owners on them first.</div>}
              {people.map((c) => (
                <div key={c.key} style={{ ...pickRow, cursor: c.email ? "pointer" : "default" }} onClick={() => toggle(c.key)} role="checkbox" aria-checked={picked.includes(c.key)}>
                  <input type="checkbox" checked={picked.includes(c.key)} disabled={!c.email} readOnly style={check} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{c.contact || c.email}{c.title ? <span style={{ fontWeight: 400 }}>{` · ${c.title}`}</span> : null}</div>
                    {c.email ? (
                      <div style={sub}>{c.contact ? c.email : ""}</div>
                    ) : (
                      <Flex sx={{ gap: "6px", marginTop: "4px" }} onClick={(e) => e.stopPropagation()}>
                        <input style={{ ...input, padding: "4px 8px", fontSize: 12 }} placeholder="No email yet - add one" value={emailFor[c.key] ?? ""} onChange={(e) => setEmailFor((m) => ({ ...m, [c.key]: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") giveEmail(c); }} />
                        <BasicButton outline scheme="secondary" type="button" keepText onClick={() => giveEmail(c)}>Add</BasicButton>
                      </Flex>
                    )}
                    <div style={{ ...sub, fontSize: 11 }}>{`found on ${c.site}`}</div>
                  </div>
                </div>
              ))}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <label style={{ ...label, marginTop: 0 }}>From</label>
              <select style={input} value={senderId} onChange={(e) => setSenderId(e.target.value)}>
                <option value="">Choose who it comes from</option>
                {senders.map((s) => <option key={s.id} value={s.id}>{s.name}{s.email ? ` (${s.email})` : ""}</option>)}
              </select>
              <div style={{ ...sub, marginTop: 4 }}>Signs the email and the proposal, and gets the replies.</div>

              <label style={label}>Email</label>
              <input style={input} value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Subject" />
              <textarea style={{ ...input, minHeight: 200, marginTop: 8, lineHeight: 1.5 }} value={message} onChange={(e) => setMessage(e.target.value)} aria-label="Message" />
              <div style={{ ...sub, marginTop: 4 }}>{"{firstName}"} becomes each person&apos;s first name. The email adds a button and their own link.</div>

              {chosen.length > 0 && (
                <Box sx={{ marginTop: "10px", border: "1px solid #EEF0F2", borderRadius: "8px", padding: "10px 12px", background: "#FAFBFC" }}>
                  <div style={{ ...sub, marginBottom: 6 }}>{`As sent to ${chosen[0].contact || chosen[0].email}`}</div>
                  <div style={{ fontFamily: "Poppins", fontSize: 12, color: "#222", whiteSpace: "pre-line", lineHeight: 1.55 }}>{fill(message, chosen[0].contact)}</div>
                </Box>
              )}

              <div style={{ ...sub, marginTop: 8 }}>
                {preparing ? "Building the combined proposal…" : prepared ? (
                  <a href={`/proposals/${prepared.proposalId}`} target="_blank" rel="noreferrer" style={{ color: "#185FA5" }}>{`Preview the proposal (${sites.length} sites)`}</a>
                ) : null}
              </div>
              <div style={{ ...pickRow, borderBottom: "none", color: themevals.colors.dkgrey }} onClick={() => setIncludePassword((v) => !v)} role="checkbox" aria-checked={includePassword}>
                <input type="checkbox" checked={includePassword} readOnly style={check} />
                <span>Put the password in the email. Off sends it in a second email on its own.</span>
              </div>
            </Box>
          </Flex>
        )}

        {error && <Box sx={{ marginTop: "12px", padding: "10px 14px", background: "#FBEAEA", color: "#A60000", borderRadius: "8px", fontFamily: "Poppins", fontSize: 13 }}>{error}</Box>}

        {!loading && !done && (
          <Flex sx={{ justifyContent: "space-between", gap: "8px", marginTop: "18px", paddingTop: "14px", borderTop: "1px solid #EEF0F2", flexWrap: "wrap" }}>
            <BasicButton outline scheme="secondary" type="button" keepText onClick={() => { if (preparedRef.current) deleteProposal(preparedRef.current); onClose(false); }}>Cancel</BasicButton>
            <BasicButton type="button" keepText disabled={!canSend} onClick={send}>
              {busy ? "Building the proposal and sending…" : chosen.length ? `Send one proposal to ${chosen.length} ${chosen.length === 1 ? "person" : "people"}` : "Pick who gets it"}
            </BasicButton>
          </Flex>
        )}
      </Box>
    </>
  );
};

export default CombineSend;
